import { useCallback, useEffect, useRef, useState } from 'react';
import {
  Button,
  Checkbox,
  ContentSwitcher,
  InlineLoading,
  Switch,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
  Tag,
} from '@carbon/react';
import { Renew } from '@carbon/icons-react';
import { api, Permission, QueueJob, QueueName, QueueStats } from '../lib/api';
import { formatNumber, formatPeriod, JOB_LABELS, JOB_TONE, relativeTime, waitingFor } from '../lib/format';
import { DEFAULT_PAGE_SIZE, lastPageIndex } from '../lib/pagination';
import { Card, Stat, TablePagination, useToast } from '../components/ui';

const QUEUES: Array<{ value: QueueName | 'all'; label: string; blurb: string }> = [
  { value: 'all', label: 'Everything', blurb: 'Both queues' },
  {
    value: 'inbound',
    label: 'Inbound',
    blurb: 'Reports received from facilities, waiting to be translated',
  },
  {
    value: 'outbound',
    label: 'Outbound',
    blurb: 'Translated payloads waiting to be sent to DHIS2',
  },
];

/**
 * What is waiting, and what is stuck.
 *
 * Work is queued in two stages rather than done inline, so this screen is the honest answer to
 * "has my data gone yet?" - a submission can be accepted, durable and still not delivered.
 */
export function Queue({ can }: { can: (permission: Permission) => boolean }) {
  const [queue, setQueue] = useState<QueueName | 'all'>('all');
  const [pendingOnly, setPendingOnly] = useState(true);
  const [jobs, setJobs] = useState<QueueJob[]>([]);
  const [stats, setStats] = useState<QueueStats | null>(null);
  const [total, setTotal] = useState(0);
  const [page, setPage] = useState(0);
  const [pageSize, setPageSize] = useState(DEFAULT_PAGE_SIZE);
  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState<number | null>(null);
  const latestRequest = useRef(0);
  const toast = useToast();

  const canAct = can('queue.manage');

  const load = useCallback(async () => {
    const request = ++latestRequest.current;
    try {
      const params = new URLSearchParams({
        limit: String(pageSize),
        offset: String(page * pageSize),
      });
      if (queue !== 'all') params.set('queue', queue);
      if (pendingOnly) params.set('pendingOnly', 'true');
      const [list, depth] = await Promise.all([
        api.get<{ rows: QueueJob[]; total: number }>(`/api/queue?${params.toString()}`),
        api.get<QueueStats>('/api/queue/stats'),
      ]);
      // A poll still in flight when the page or filter changed must not overwrite the newer answer.
      if (request !== latestRequest.current) return;
      setTotal(list.total);
      setStats(depth);
      const lastPage = lastPageIndex(list.total, pageSize);
      if (page > lastPage) {
        // Jobs finished or were cancelled out from under this page. Step back to the last page that
        // still has rows, keeping the current rows on screen until it arrives.
        setPage(lastPage);
        return;
      }
      setJobs(list.rows);
    } catch (error) {
      toast('error', error instanceof Error ? error.message : String(error));
    } finally {
      if (request === latestRequest.current) setLoading(false);
    }
  }, [queue, pendingOnly, page, pageSize, toast]);

  useEffect(() => {
    load();
    // A queue view is only useful if it is live; this is the one screen worth polling briskly.
    const timer = setInterval(load, 5000);
    return () => clearInterval(timer);
  }, [load]);

  async function act(id: number, action: 'requeue' | 'cancel') {
    setBusy(id);
    try {
      await api.post(`/api/queue/${id}/${action}`);
      toast('ok', action === 'requeue' ? `Job ${id} queued again` : `Job ${id} cancelled`);
      await load();
    } catch (error) {
      toast('error', error instanceof Error ? error.message : String(error));
    } finally {
      setBusy(null);
    }
  }

  const inbound = stats?.inbound;
  const outbound = stats?.outbound;
  const stuck = (inbound?.dead ?? 0) + (outbound?.dead ?? 0);

  return (
    <>
      <div className="grid grid-stats" style={{ marginBottom: 20 }}>
        <Stat
          label="Waiting to translate"
          value={formatNumber(inbound?.pending ?? 0)}
          meta={
            inbound?.running
              ? `${inbound.running} in progress`
              : inbound?.oldestPendingAt
                ? `Oldest waiting ${waitingFor(inbound.oldestPendingAt)}`
                : 'Inbound queue clear'
          }
          tint={inbound?.pending ? 'var(--accent)' : undefined}
        />
        <Stat
          label="Waiting for DHIS2"
          value={formatNumber(outbound?.pending ?? 0)}
          meta={
            outbound?.running
              ? `${outbound.running} being sent`
              : outbound?.oldestPendingAt
                ? `Oldest waiting ${waitingFor(outbound.oldestPendingAt)}`
                : 'Outbound queue clear'
          }
          tint={outbound?.pending ? 'var(--accent)' : undefined}
        />
        <Stat
          label="Given up"
          value={formatNumber(stuck)}
          meta={stuck > 0 ? 'Needs a look, then requeue' : 'Nothing abandoned'}
          tint={stuck > 0 ? 'var(--danger)' : undefined}
        />
        <Stat
          label="Completed"
          value={formatNumber((inbound?.succeeded ?? 0) + (outbound?.succeeded ?? 0))}
          meta="Jobs finished across both queues"
          tint={(inbound?.succeeded ?? 0) + (outbound?.succeeded ?? 0) > 0 ? 'var(--ok)' : undefined}
        />
      </div>

      <div className="queue-toolbar">
        <div className="queue-toolbar-left">
          <ContentSwitcher
            className="queue-scope-switcher"
            selectedIndex={QUEUES.findIndex((q) => q.value === queue)}
            onChange={({ index }) => {
              if (typeof index === 'number' && QUEUES[index]) {
                setQueue(QUEUES[index].value);
                setPage(0);
              }
            }}
            aria-label="Queue scope switcher"
          >
            {QUEUES.map((option) => (
              <Switch key={option.value} name={option.value} text={option.label} />
            ))}
          </ContentSwitcher>
          <span className="queue-toolbar-helper">
            {QUEUES.find((q) => q.value === queue)?.blurb}
          </span>
        </div>

        <div className="queue-toolbar-right">
          <Checkbox
            id="queue-pending-only"
            labelText="Only work still waiting"
            checked={pendingOnly}
            onChange={(_event, { checked }) => {
              setPendingOnly(checked);
              setPage(0);
            }}
          />
          <Button
            kind="ghost"
            size="md"
            renderIcon={Renew}
            onClick={load}
          >
            Refresh
          </Button>
        </div>
      </div>

      <Card bodyless>
        <div className="queue-results-panel">
          {loading ? (
            <div className="queue-results-empty">
              <InlineLoading description="Loading the queue…" />
            </div>
          ) : jobs.length === 0 ? (
            <div className="queue-results-empty">
              <div className="queue-empty-title">
                {pendingOnly ? 'Nothing is waiting' : 'No jobs match'}
              </div>
              <div className="queue-empty-desc">
                {pendingOnly
                  ? 'Everything facilities have sent has been translated and delivered.'
                  : 'Try turning off the "still waiting" filter.'}
              </div>
            </div>
          ) : (
            <div className="table-wrap">
              <Table size="sm">
                <TableHead>
                  <TableRow>
                    <TableHeader>Job</TableHeader>
                    <TableHeader>Stage</TableHeader>
                    <TableHeader>Facility</TableHeader>
                    <TableHeader>Period</TableHeader>
                    <TableHeader>State</TableHeader>
                    <TableHeader className="num">Attempts</TableHeader>
                    <TableHeader>Queued</TableHeader>
                    <TableHeader />
                  </TableRow>
                </TableHead>
                <TableBody>
                  {jobs.map((job) => (
                    <TableRow key={job.id}>
                      <TableCell className="mono faint">#{job.id}</TableCell>
                      <TableCell>
                        <Tag type="gray">
                          {job.queue === 'inbound' ? 'Translate' : 'Send to DHIS2'}
                        </Tag>
                      </TableCell>
                      <TableCell>
                        <div className="stack">
                          <span style={{ fontWeight: 560 }}>{job.facilityName}</span>
                          <span className="faint small mono">{job.facilityCode}</span>
                        </div>
                      </TableCell>
                      <TableCell>{formatPeriod(job.period)}</TableCell>
                      <TableCell>
                        <div className="stack">
                          <span className={`pill pill-${JOB_TONE[job.status] ?? 'neutral'}`}>
                            {JOB_LABELS[job.status] ?? job.status}
                          </span>
                          {job.lastError ? (
                            <span
                              className="faint small truncate"
                              title={job.lastError}
                              style={{ maxWidth: 260 }}
                            >
                              {job.lastError}
                            </span>
                          ) : null}
                        </div>
                      </TableCell>
                      <TableCell className="num">
                        {job.attempts}/{job.maxAttempts}
                      </TableCell>
                      <TableCell className="muted">{relativeTime(job.createdAt)}</TableCell>
                      <TableCell>
                        {canAct ? (
                          <div className="row" style={{ justifyContent: 'flex-end', gap: 8 }}>
                            {job.status === 'dead' || job.status === 'failed' ? (
                              <Button
                                kind="tertiary"
                                size="sm"
                                disabled={busy === job.id}
                                onClick={() => act(job.id, 'requeue')}
                              >
                                Try again
                              </Button>
                            ) : null}
                            {job.status === 'pending' || job.status === 'dead' ? (
                              <Button
                                kind="danger--ghost"
                                size="sm"
                                disabled={busy === job.id}
                                onClick={() => act(job.id, 'cancel')}
                              >
                                Cancel
                              </Button>
                            ) : null}
                          </div>
                        ) : null}
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </div>
          )}
          {!loading && jobs.length > 0 ? (
            <TablePagination
              page={page}
              pageSize={pageSize}
              totalItems={total}
              onChange={(nextPage, nextPageSize) => {
                setPage(nextPage);
                setPageSize(nextPageSize);
              }}
            />
          ) : null}
        </div>
      </Card>
    </>
  );
}
